import { Tooltip } from 'antd';
import Color from 'color';
import { Dictionary, isEmpty } from 'lodash';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  FormInstance,
  FormInstanceApi,
  FormQuestion,
  FormResponseType,
  FormTemplate,
  WebFormAnswer,
  WebFormTaskDetails,
} from 'api/form';
import AcmeLogo from 'assets/acme_logo_64px.png';
import { ReactComponent as BackFront } from 'assets/svgs/back-and-front.svg';
import { ReactComponent as RocketlaneLogoSvg } from 'assets/svgs/rocketlane_logo_black.svg';
import { Alert } from 'components/atoms/alert';
import { Avatar } from 'components/atoms/avatar';
import { Button } from 'components/atoms/button';
import { FormHeader } from 'components/molecules/forms/form-fields/form-header';
import { FormSection } from 'components/molecules/forms/form-fields/styles';
import { toastService } from 'components/molecules/toast';
import { computeFormBgImageBrightness } from 'components/organisms/templates/forms/utils';
import { Caption01, ProductiveHeading05 } from 'components/styles';
import { VariablesContext } from 'contexts/variables';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useFormUnload } from 'hooks/use-form-unload';
import { deleteFormResponseFromIndexedDb } from 'hooks/use-idb';
import { useTranslate } from 'hooks/use-translation';
import { useIsUserAnonymousSafe } from 'hooks/use-user';
import { LastEditedBy } from './form-components';
import { FormQuestionRenderer } from './form-renderer';
import {
  computeFormAnswerPayload,
  computeFormDefaultValues,
  FormResponse,
  resetQuestionsToEmptyValuesUtil,
  saveFormRespToIndexedDb,
  setValuesInForm,
  validateAttachmentUploading,
} from './utils';

interface Props {
  flattenedQuestionsMap?: Dictionary<FormQuestion>;
  formTemplate: FormTemplate;
  isPreviewMode?: boolean;
  companyName: string;
  companyLogoUrl: string;
  formAnswerData?: WebFormAnswer;
  taskDetails?: WebFormTaskDetails;
  formInstance?: FormInstance;
  setIsSubmitting?: React.Dispatch<React.SetStateAction<boolean>>;
  onAnswerSubmitFailure?: (error: Error) => void;
  onAnswerSubmitSuccess?: () => void;
}

const PreviewProjectName = 'Acme project';

const Wrapper = styled.div<{
  backgroundColor: string;
  backgroundImage: string;
}>`
  position: relative;
  height: 100%;
  overflow-y: scroll;
  background-color: ${(props) =>
    !props.backgroundImage && `${props.backgroundColor}`};
  @media screen and (max-width: 480px) {
    overflow-y: hidden;
  }
`;
const WrapperContent = styled.div<{
  backgroundImage: string;
  backgroundBrightnessOverlay: string;
}>`
  background: ${(props) =>
    props.backgroundImage &&
    `url(${props.backgroundImage}) no-repeat center center fixed`};
  background-size: cover;
  height: 100%;
  position: relative;
  overflow-y: hidden;
  &::before {
    content: '';
    position: absolute;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    background-color: ${(props) =>
      props.backgroundImage &&
      props.backgroundBrightnessOverlay &&
      `${props.backgroundBrightnessOverlay}`};
  }
`;
const FormWrapper = styled.div`
  position: relative;
  max-width: 800px;
  margin: 70px auto 150px;
  padding: 0 var(--spacing-05);
`;
const FormElements = styled.div`
  section {
    ${FormSection};
    margin-top: var(--spacing-05);
    position: relative;
    @media screen and (max-width: 480px) {
      padding: var(--spacing-05);
    }
  }
`;
const StyledAlert = styled(Alert)`
  margin-bottom: var(--spacing-05);
`;
const PrimaryButton = styled(Button)<{
  buttonColor: string;
  hoveBgColor: string;
}>`
  &&:not(:disabled) {
    background-color: ${({ buttonColor }) => buttonColor};
    border-color: ${({ buttonColor }) => buttonColor};
    color: var(--white-100);
  }
  &&:hover,
  &&:active {
    background-color: ${({ hoveBgColor }) => hoveBgColor};
    color: var(--white-100);
  }
`;
const SecondaryButton = styled(Button)<{ buttonColor: string }>`
  color: ${({ buttonColor }) => buttonColor};
  border-color: ${({ buttonColor }) => buttonColor};
  &&:hover,
  &&:active {
    background-color: ${({ buttonColor }) => buttonColor};
    color: var(--white-100);
  }
  @media screen and (max-width: 480px) {
    max-width: 50%;
  }
`;
const FormContentFooterWrapper = styled.div`
  background: var(--white-100);
  padding: var(--spacing-06);
  border-radius: 0px 0px 10px 10px;
  margin-top: var(--spacing-05);
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  @media screen and (max-width: 480px) {
    padding: var(--spacing-05);
  }
`;
const FormActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  @media screen and (max-width: 480px) {
    flex-direction: column;
    > * {
      width: 100%;
    }
  }
`;
const FormActions = styled.div`
  display: flex;
  justify-content: space-between;
  flex-grow: 1;
  gap: var(--spacing-03);
  ${PrimaryButton}:disabled, ${SecondaryButton}:disabled {
    pointer-events: none;
    opacity: 0.8;
  }
  @media screen and (max-width: 480px) {
    justify-content: center;
    > * {
      flex-grow: 1;
    }
  }
`;
const ProjectDetailsWrapper = styled.div`
  margin-bottom: var(--spacing-08);
  display: flex;
  align-items: center;
  gap: var(--spacing-05);
`;
const LogoItem = styled.div`
  width: 48px;
  height: 48px;
  img {
    border-radius: var(--border-radius-base);
  }
`;
const SquareAvatar = styled(Avatar)`
  border-radius: var(--border-radius-base);
  background-color: inherit !important;
`;
const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;
const ProjectName = styled.div`
  ${ProductiveHeading05};
`;
const LastEditedByWrapper = styled.div`
  margin-top: var(--spacing-05);
  display: flex;
  justify-content: flex-end;
`;
const FooterWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--white-100);
  height: 40px;
  align-items: center;
  display: flex;
  ${Caption01};
`;
const PoweredByWrapper = styled.div`
  margin: 0 auto;
  display: flex;
  align-items: center;
  svg {
    opacity: 0.7;
    height: 14px;
  }
`;

export const WebFormRenderer: FC<Props> = ({
  flattenedQuestionsMap,
  formTemplate,
  formAnswerData,
  taskDetails,
  isPreviewMode = false,
  companyName,
  companyLogoUrl,
  formInstance,
  setIsSubmitting,
  onAnswerSubmitSuccess,
  onAnswerSubmitFailure,
}) => {
  const isAnonymous = useIsUserAnonymousSafe();
  const translate = useTranslate();
  // this ref is used to check if the response has been submitted, if it has then the response shouldn't be saved in the indexedDb
  const isDeletedRef = useRef(false);
  const isEditingRef = useRef(false);
  const formDefaultValues = useMemo(() => {
    if (formAnswerData) {
      return computeFormDefaultValues(formAnswerData.answerMetaJson);
    }

    return {};
  }, [formAnswerData]);
  const {
    control,
    setValue,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<Record<string, string | number | number[]>>({
    defaultValues: formDefaultValues,
    shouldFocusError: false,
  });

  const isMultiResponseMode =
    formTemplate.formSettings.formResponseType ===
      FormResponseType.MultipleResponse ?? false;
  const [isEditing, setIsEditing] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  const isFormEditAllowed = !isMultiResponseMode && !isEmpty(formAnswerData);
  const showLastEditedByData =
    !isMultiResponseMode &&
    isFormEditAllowed &&
    !isEditing &&
    !isEmpty(formAnswerData);

  const PrimaryButtonHoverBgColor = useMemo(() => {
    return Color(formTemplate.formTheme.buttonColor).darken(0.2).hex();
  }, [formTemplate.formTheme.buttonColor]);

  useEffect(() => {
    setValuesInForm(
      formInstance?.formInstanceId,
      formAnswerData?.updatedAt,
      formDefaultValues,
      reset,
      setIsEditing,
      setShowBanner,
    );
  }, [
    formAnswerData?.updatedAt,
    formDefaultValues,
    formInstance?.formInstanceId,
    reset,
  ]);

  const brightnesssOverlay = useMemo(() => {
    return formTemplate.formTheme.brightness
      ? computeFormBgImageBrightness(formTemplate.formTheme.brightness)
      : '';
  }, [formTemplate.formTheme.brightness]);

  const resetQuestionsToEmptyValues = useCallback(async () => {
    resetQuestionsToEmptyValuesUtil(flattenedQuestionsMap, reset);
    if (formInstance?.formInstanceId != null) {
      if (formAnswerData?.answerId) {
        return;
      }
      deleteFormResponseFromIndexedDb(formInstance?.formInstanceId);
      isDeletedRef.current = true;
    }
  }, [
    flattenedQuestionsMap,
    formAnswerData?.answerId,
    formInstance?.formInstanceId,
    reset,
  ]);

  const clearFormConfirmation = useDeleteConfirmModal({
    title: `${translate('clear_form')}?`,
    content: translate('forms.response.clear_form_message'),
    okText: translate('clear'),
    onOk: () => {
      resetQuestionsToEmptyValues();
    },
  });

  const onFormSubmit = (data: FormResponse) => {
    const isAttachmentUploading = validateAttachmentUploading(data);

    if (isAttachmentUploading) {
      toastService({
        title: translate('forms.response.attachment_uploading_title'),
        message: translate('forms.response.attachment_uploading_message'),
      }).info();

      return null;
    }

    const formAnswerPayload = computeFormAnswerPayload(
      data,
      flattenedQuestionsMap!,
    );

    setIsSubmitting?.(true);
    if (formAnswerData) {
      return FormInstanceApi.updateWebFormAnswer({
        answerId: formAnswerData.answerId,
        formInstanceId: formInstance!.formInstanceId,
        answerMetaJson: formAnswerPayload,
      })
        .then(async () => {
          onAnswerSubmitSuccess?.();
          if (formInstance?.formInstanceId) {
            await deleteFormResponseFromIndexedDb(formInstance?.formInstanceId);
            isDeletedRef.current = true;
          }
        })
        .catch((error) => {
          onAnswerSubmitFailure?.(error);
        });
    }

    return FormInstanceApi.createWebFormAnswer({
      formInstanceId: formInstance!.formInstanceId,
      answerMetaJson: formAnswerPayload,
    })
      .then(async () => {
        onAnswerSubmitSuccess?.();
        if (formInstance?.formInstanceId) {
          await deleteFormResponseFromIndexedDb(formInstance?.formInstanceId);
          isDeletedRef.current = true;
        }
      })
      .catch((error) => {
        onAnswerSubmitFailure?.(error);
      });
  };

  const onSubmit = (data: FormResponse) => {
    if (!isPreviewMode) {
      return onFormSubmit?.(data);
    }

    return null;
  };

  const onChange = useCallback(
    async (data: FormResponse) => {
      if (formInstance?.formInstanceId != null) {
        saveFormRespToIndexedDb(
          data,
          isMultiResponseMode,
          formInstance?.formInstanceId,
          formAnswerData?.answerId,
        );
      }
    },
    [
      formAnswerData?.answerId,
      formInstance?.formInstanceId,
      isMultiResponseMode,
    ],
  );

  useEffect(() => {
    return () => {
      if (
        (isEditingRef.current === true || isMultiResponseMode) &&
        isDeletedRef.current === false
      ) {
        onChange(control._formValues);
      }
    };
  }, [control._formValues, isMultiResponseMode, onChange]);

  useEffect(() => {
    if (isEmpty(formAnswerData)) {
      isEditingRef.current = true;
    } else {
      isEditingRef.current = isEditing;
    }
  }, [formAnswerData, isEditing]);

  useEffect(() => {
    setTimeout(() => {
      setShowBanner(false);
    }, 4000);
  }, []);

  const disabledButtonTooltip = translate('form_templates.submit_disabled');

  const refreshSave = useCallback(() => {
    if (isDeletedRef.current === false && isEditingRef.current === true) {
      onChange(control._formValues);
    }
  }, [control._formValues, onChange]);

  const cancelEditForm = async () => {
    isDeletedRef.current = true;
    setIsEditing(false);
    if (formInstance?.formInstanceId) {
      await deleteFormResponseFromIndexedDb(formInstance?.formInstanceId);
    }
    reset(formDefaultValues);
  };

  useFormUnload(refreshSave);

  return (
    <VariablesContext.Provider
      value={{
        allowSimpleVariables: !isAnonymous,
        resolveVariablesToFallback: !isAnonymous,
      }}
    >
      <Wrapper
        backgroundColor={formTemplate.formTheme.backgroundColor}
        backgroundImage={formTemplate.formTheme.backgroundImage ?? ''}
      >
        <WrapperContent
          backgroundImage={formTemplate.formTheme.backgroundImage ?? ''}
          backgroundBrightnessOverlay={brightnesssOverlay}
        >
          <FormWrapper>
            {showBanner && (
              <StyledAlert
                showIcon
                type="warning"
                message={<FormattedMessage id="forms.response.not_submitted" />}
              />
            )}
            <ProjectDetailsWrapper>
              <LogoWrapper>
                <LogoItem>
                  <SquareAvatar
                    size={48}
                    name={companyName}
                    src={companyLogoUrl}
                  />
                </LogoItem>
                <BackFront />
                <LogoItem>
                  {isPreviewMode ? (
                    <img
                      src={AcmeLogo}
                      width={48}
                      height={48}
                      alt="customer company logo"
                    />
                  ) : (
                    <SquareAvatar
                      size={48}
                      name={taskDetails?.customerCompanyName}
                      src={taskDetails?.customerCompanyLogoUrl}
                    />
                  )}
                </LogoItem>
              </LogoWrapper>
              <ProjectName>
                {isPreviewMode ? PreviewProjectName : taskDetails?.projectName}
              </ProjectName>
            </ProjectDetailsWrapper>
            <FormHeader
              name={formTemplate.name}
              description={formTemplate.description}
              formTheme={formTemplate.formTheme}
            />
            <FormElements>
              {formTemplate.formQuestions.map((question) => (
                <FormQuestionRenderer
                  question={question}
                  control={control}
                  setValue={setValue}
                  errors={{
                    ...errors,
                  }}
                  disabled={isFormEditAllowed && !isEditing}
                />
              ))}
            </FormElements>
            <FormContentFooterWrapper>
              {isFormEditAllowed && !isEditing ? (
                <PrimaryButton
                  disabled={isPreviewMode}
                  buttonColor={formTemplate.formTheme.buttonColor}
                  hoveBgColor={PrimaryButtonHoverBgColor}
                  onClick={() => setIsEditing(true)}
                >
                  <FormattedMessage id="Edit response" />
                </PrimaryButton>
              ) : (
                <FormActionsWrapper>
                  <FormActions>
                    <Button onClick={() => cancelEditForm()}>
                      <FormattedMessage id="cancel" />
                    </Button>
                    <SecondaryButton
                      buttonColor={formTemplate.formTheme.buttonColor}
                      onClick={() => clearFormConfirmation()}
                      disabled={!formTemplate.formQuestions.length}
                    >
                      <FormattedMessage id="clear_form" />
                    </SecondaryButton>
                  </FormActions>
                  <Tooltip title={isPreviewMode ? disabledButtonTooltip : null}>
                    <PrimaryButton
                      disabled={
                        isPreviewMode || !formTemplate.formQuestions.length
                      }
                      buttonColor={formTemplate.formTheme.buttonColor}
                      hoveBgColor={PrimaryButtonHoverBgColor}
                      onClick={handleSubmit(onSubmit)}
                    >
                      <FormattedMessage id="submit" />
                    </PrimaryButton>
                  </Tooltip>
                </FormActionsWrapper>
              )}
              {showLastEditedByData && (
                <LastEditedByWrapper>
                  <LastEditedBy
                    submittedBy={formAnswerData?.lastActedUser}
                    submittedAt={
                      formAnswerData?.updatedAt || formAnswerData?.createdAt
                    }
                  />
                </LastEditedByWrapper>
              )}
            </FormContentFooterWrapper>
          </FormWrapper>
          <FooterWrapper>
            <PoweredByWrapper>
              <FormattedMessage id="powered_by" />
              <a
                href="https://www.rocketlane.com"
                target="_blank"
                rel="noreferrer noopener"
              >
                <RocketlaneLogoSvg />
              </a>
            </PoweredByWrapper>
          </FooterWrapper>
        </WrapperContent>
      </Wrapper>
    </VariablesContext.Provider>
  );
};
